<template>
  <div @click.prevent.stop="onClick">
    <div :style="wrapperStyle" class="responsive-drawer-header" :class="`${theme.appBar.other.bg} ${theme.appBar.other.color}--text`">
      <!-- The drawer is persistent on lg and xl devices, collapsible on smaller devices -->
      <!-- The drawer can be omitted if the property "hasDrawer" is true                -->
      <div v-if="hasDrawer" :style="drawerStyle" :class="`${drawerClasses}`" class="drawer">
        <div class="drawer-content">
          <div class="drawer-content-title text--h5" :class="$slots['drawer-subtitle'] ? '' : 'my-auto pt-1'">
            <!-- The module title                                     -->
            <div @click="oneLevelBack()" class="touch module-title">
              <slot name="drawer-module-title"></slot>
            </div>
            <!-- The badge displaying the moduleUnreadCount           -->
            <number-badge v-if="moduleUnreadCount > 0" :offsetY="-4" :badgeColor="theme.unreadBadgeColor" :count="moduleUnreadCount"></number-badge>
          </div>

          <!-- The subtitle                                           -->
          <div
            v-if="$slots['drawer-subtitle']"
            @click="oneLevelBack()"
            class="touch drawer-content-subtitle op-text-sm"
            :style="{ marginLeft: mx_needsDrawer ? 35 : 0 }"
          >
            <slot name="drawer-subtitle"></slot>
          </div>

          <!-- The drawer-command-slot                                -->
          <!-- The "drawer-command-slot" is a 30px wide               -->
          <!-- slot to take a single icon oder icon menu, eg. sorting -->
          <div v-if="$slots['drawer-command-slot']" class="drawer-command-slot touch">
            <slot name="drawer-command-slot"></slot>
          </div>
        </div>
        <div v-if="!mx_needsDrawer" class="arrow-back" :style="arrowBackDrawerStyle">
          <v-btn small icon :color="theme.appBar.other.color" @click="oneLevelBack()">
            <!--          <v-btn :small="!mx_needsDrawer" outlined icon :color="theme.appBar.other.color" @click="oneLevelBack()">-->
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Content area is displayed next to the drawer on lg and xl devices or beneath the drawer on smaller devices -->
      <!-- On lg and xl devices the content area is splitted into a left side (permanent drawer) and a right part -->
      <!-- On smaller devices there is no left side -->
      <!-- An alternative is to f-->
      <div :style="contentStyle" :class="contentClass">
        <div class="content-left-container">
          <!-- First row in case drawer is closed or the header has no drawer-->
          <div class="two-rows pl-1">
            <div
              v-if="mx_needsDrawer || !hasDrawer"
              style="width: max-content"
              class="text--h5 touch module-title d-flex align-center"
              @click="$router.push('/')"
              :class="hasDrawer ? '' : 'pl-3'"
            >
              <!-- The module title                                     -->
              <slot name="left-module-title"></slot>

              <!-- The badge displaying the moduleUnreadCount           -->
              <number-badge v-if="moduleUnreadCount > 0" :offsetY="-4" :badgeColor="theme.unreadBadgeColor" :count="moduleUnreadCount"></number-badge>
            </div>
            <!-- Left does not display module title on lg and xl. Add placeholder to align subtitle at the bottom-->
            <div v-if="!mx_needsDrawer && hasDrawer" class="text--h5">&nbsp;</div>

            <div
              v-if="$slots['left-subtitle']"
              class="d-flex left-subtitle"
              :class="hasDrawer ? '' : 'pl-3'"
              style="width: auto"
              :style="{ marginLeft: mx_isLeftDrawerOpen === false ? 35 : 0 }"
            >
              <slot name="left-subtitle" class="op-text"></slot>
            </div>
          </div>
        </div>
        <div v-if="$slots['right']" class="content-right-container">
          <slot name="right"></slot>
        </div>
        <!-- vertical divider separates module specific tools with global tools -->
        <div v-if="!xs" class="border-right" :class="`${theme.appBar.other.bg} lighten-1 mx-2`"></div>
        <div v-else class="mr-1"></div>
      </div>
      <div v-if="hasContentBackButton" class="arrow-back-content" :style="arrowBackContentStyle">
        <v-btn :color="theme.appBar.other.color" small icon @click="oneLevelBack()">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Drawer open/close toggle -->
    <div v-if="mx_needsDrawer && hasDrawerSlot && hasDrawer" :class="`${appBarColor} toggle-wrapper touch d-flex flex-column`" @click="toggleDrawer">
      <morph-icon
        :color="color"
        :fromIcon="closeDrawerIndicatorIcon"
        :toIcon="openDrawerIndicatorIcon"
        :showHint="false"
        :size="30"
        :trigger="mx_isLeftDrawerOpen"
      />
    </div>
  </div>
</template>

<script>
import common from '@/mixins/common';
import { drawerWidth, drawerWidth_xl } from '@/common/constants';
import configMixin from '@/mixins/configMixin';
import MorphIcon from '@/components/icons/MorphIcon.vue';
import responsiveDrawerLayoutMixin from './responsiveDrawerLayoutMixin';
import NumberBadge from '@/components/decorators/NumberBadge';

export default {
  name: 'ResponsiveDrawerHeader',
  mixins: [common, configMixin, responsiveDrawerLayoutMixin],
  components: {
    MorphIcon,
    NumberBadge,
  },
  props: {
    drawerWidth: { type: Number, default: drawerWidth },
    drawerWidth_xl: { type: Number, default: drawerWidth_xl },
    openDrawerIcon: { type: String, default: 'mdi-menu' },
    // openDrawerIcon: { type: String, default: 'mdi-file-tree' },
    // closeDrawerIcon: { type: String, default: 'mdi-chevron-left' },
    closeDrawerIcon: { type: String, default: 'mdi-close' },

    color: { type: String, default: 'white' },
    oneLine: { type: Boolean, default: false },
    hasDrawer: { type: Boolean, default: true },
    moduleUnreadCount: Number,
    isItemDisplayed: { type: Boolean, default: true }, // optional property to help decide if the drawer toggle should go back to root
    // in case of no item is currently displayed
  },

  data() {
    return {
      closeDrawerIndicatorIcon: this.mx_isLeftDrawerOpen ? this.closeDrawerIcon : this.openDrawerIcon,
      openDrawerIndicatorIcon: this.mx_isLeftDrawerOpen ? this.openDrawerIcon : this.closeDrawerIcon,
    };
  },
  mounted() {
    // Close drawer when mounted
    this.mx_isLeftDrawerOpen = false;
  },
  computed: {
    hasContentBackButton() {
      // Only show content arrow when drawer arrow isn't shown
      return (this.mx_needsDrawer || !this.hasDrawer) && (!this.xs || (this.xs && this.mx_isLeftDrawerOpen) || !this.hasDrawer);
    },
    hasDrawerSlot() {
      // return true; // An empty drawer slot leads to misinterpretation and therefore the rendering should always be with a drawer slot
      //return !!this.$slots['drawer'];
      return this.hasDrawer;
    },
    wrapperStyle() {
      let style = { maxWidth: null };
      if (this.xs) {
        // style.right = this.mx_isLeftDrawerOpen ? '0vw ' : '70px';
        // new since 1.4.43: on xs devices no slot for search and hamburger
        style.right = '0vw ';
      } else {
        style.right = '113px';
      }

      return style;
    },

    drawerClasses() {
      if (this.xs) {
        return `${this.appBarColor} ${this.mx_isLeftDrawerOpen ? 'drawer-shadow-xs' : ''}`;
      } else {
        return `${this.appBarColor} ${this.mx_needsDrawer ? 'drawer-shadow' : ''}`;
      }
    },
    drawerStyle() {
      let style = {};
      //console.debug('drawerStyle this.mx_needsDrawer', this.mx_needsDrawer, 'this.hasDrawerSlot', this.hasDrawerSlot);
      if (!this.hasDrawer) {
        console.debug('no drawer slot');
      } else if (!this.mx_needsDrawer) {
        //lg and xl: content-persistent-drawer
        style.width = `${this.mx_currentDrawerWidthStr} !important`;
      } else if (this.xs) {
        style.paddingLeft = this.mx_isLeftDrawerOpen ? '35px !important' : '55px !important';
        style.transform = this.mx_isLeftDrawerOpen ? 'translateX(0)' : `translateX(-100vw)`;
      } else if (this.mdAndDown) {
        // sm, md
        style.width = `calc(${this.mx_currentDrawerWidth}px) !important`;
        style.transform = this.mx_isLeftDrawerOpen ? `translateX(0)` : `translateX(-${this.mx_currentDrawerWidthStr})`;
      }

      return style;
    },
    arrowBackDrawerStyle() {
      return { top: this.mx_needsDrawer ? 0 : '18px' };
    },
    arrowBackContentStyle() {
      if (!this.hasDrawer) {
        return { left: '0px' };
      }
      return null;
    },
    contentClass() {
      if (!this.hasDrawer) {
        return 'content-no-drawer';
      }
      if (this.mx_needsDrawer) {
        return this.mdAndDown ? 'content-with-drawer' : 'content-persistent-drawer';
      } else {
        return this.mdAndDown ? 'content-no-drawer' : 'content-persistent-drawer';
      }
    },
    contentStyle() {
      if (!this.hasDrawer) {
        return { left: '20px' };
      }
      let style = {};
      if (this.xs) {
        style.left = '35px';
      } else if (this.hasDrawerSlot && !this.mx_needsDrawer && !this.mx_isLeftDrawerOpen) {
        style.left = this.mx_currentDrawerWidthStr;
      } else {
        style.left = '70px';
      }

      return style;
    },
    contentLeftStyle() {
      return '';
      //return `right:${this.xs ? 100 : 265}px`; // leaves space for 3 IconTextBtn's with 70px width and a three-dot-menu icon of 48px width
    },
    contentRightStyle() {
      return '';
      //return `width:${this.xs ? 100 : 265}px`; // same comment as above
    },
  },
  methods: {
    oneLevelBack() {
      let path = this.$route.path; //$router.back()
      console.debug('this.$route.meta', JSON.stringify(this.$route.meta, null, 2));
      if (this.$route.meta.backPath) {
        console.debug('new path', path, this.$route.meta.backPath(this.$route.params));
        this.$router.push({ path: this.$route.meta.backPath(this.$route.params) });
      } else {
        console.debug('GO TO HOME');
        this.$router.push({ path: '/' });
      }
    },
    onClick(event) {
      event.preventDefault();
    },
    onWrapperClick() {
      console.debug('onWrapperClick');
      // if (this.hasDrawerSlot && this.mx_needsDrawer) this.toggleDrawer();
    },
    toggleDrawer() {
      console.debug('toggleDrawer', this.mx_isLeftDrawerOpen, 'isItemDisplayed', this.isItemDisplayed);
      if (this.xs) {
        this.mx_isLeftDrawerOpen = !this.mx_isLeftDrawerOpen;
      } else {
        if (!this.isItemDisplayed) {
          this.$router.push({ path: '/' });
        } else {
          this.mx_isLeftDrawerOpen = !this.mx_isLeftDrawerOpen;
        }
      }
    },
  },

  watch: {
    mx_isLeftDrawerOpen(to, from) {
      this.hideKeyboard();
      // console.debug('mx_isLeftDrawerOpen', to);
    },
  },
};
</script>

<style scoped>
/* xs */
@media (max-width: 959px) {
  .drawer {
    z-index: 100000;
    width: 95vw !important;
  }
}

/* sm - small */
@media (min-width: 600px) and (max-width: 959px) {
}

/* md - medium */
@media (min-width: 960px) and (max-width: 1263px) {
}

/* md - medium */
@media (width: 980px) {
}

/* lg - large */
@media (min-width: 1264px) and (max-width: 1904px) {
}

/* xl - extra large */
@media (min-width: 1905px) {
}

/** All below 1100px width needs a drawer */
@media (max-width: 1279px) {
  .drawer-content {
    /* left: 34px; */
    left: 54px;
  }
}

/* sm to md and a bit of lg(1264-1280), since breakpoint for drawer is at 1280  */
@media (min-width: 600px) and (max-width: 1100px) {
  .drawer {
    padding-left: 55px !important;
  }
}

/** All above 1100px has a permanent drawer */
@media (min-width: 1100px) {
  .drawer {
    transform: translateX(0);
    padding-left: 20px !important;
    left: 30px;
  }

  .drawer-content {
    /* border: 1px solid purple; */
    left: 44px;
  }
}

.drawer,
.drawer-content,
.drawer-content-title,
.drawer-content-subtitle,
.arrow-back,
.arrow-back-content {
  position: absolute;
  top: 0;
  bottom: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drawer {
  background: inherit !important;
  transition: transform 200ms cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 100000;
  position: absolute;
  left: 0;

  -moz-overflow-style: none;
  white-space: nowrap;
  justify-items: center;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  /* pointer-events: auto; */
}

.drawer-shadow {
  box-shadow: 0 8px 10px -5px rgb(0 0 0 / 20%), 0 16px 24px 2px rgb(0 0 0 / 14%), 0 6px 30px 5px rgb(0 0 0 / 12%);
}

.drawer-shadow-xs {
  box-shadow: 0 8px 10px -5px rgb(0 0 0 / 20%), 0 16px 24px 2px rgb(0 0 0 / 14%), 0 6px 30px 5px rgb(0 0 0 / 12%);
}

.drawer-content {
  right: 0;
}

.drawer-content-title {
  left: 0;
  right: 36px;
  top: 0;
  height: 34px;
  align-items: center;
  display: flex;
  flex-direction: row;
}

.drawer-content-subtitle {
  left: 0;
  right: 36px;
  top: 34px;
  height: 20px;

  text-overflow: ellipsis;
}

.drawer-command-slot {
  position: absolute;
  right: 5px;
  bottom: 7px;
  top: 0;
  width: 30px;
  display: flex;
  align-items: flex-end;
}

.module-title {
  font-size: 1.1em;
}

.drawer-content-subtitle,
.drawer-content-subtitle > *,
.left-subtitle,
.left-subtitle > * {
  font-size: 0.9em; /* same size as op-text*/
  overflow: hidden;
  text-overflow: ellipsis;
}

.width-fit-content {
  width: fit-content;
}

.responsive-drawer-header {
  /* Instead of classes d-flex flex-row pa-0 mt-0 mx-xl-auto */
  display: flex !important;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;

  /* overflow-y: hidden !important; */
  overflow-y: scroll !important;
  touch-action: none !important;
  pointer-events: none !important;
  background: inherit;
  transition: transform 400ms ease;
}

.content-with-drawer,
.content-persistent-drawer,
.content-no-drawer {
  z-index: 9999;
  overflow: hidden;
  position: absolute;
  display: flex;
}

.content-with-drawer {
  right: 0;
  top: 0;
  bottom: 0;
}

.content-persistent-drawer {
  /* left to be set according to drawer width*/
  left: 50px;
  right: 0;
  top: 0;
  bottom: 0;
}

.content-no-drawer {
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
}

.left-subtitle {
  overflow: hidden;
}

.drawer > div {
  overflow-x: hidden;
  text-overflow: ellipsis;
}

.drawer::-webkit-scrollbar {
  display: none;
}

.arrow-back,
.arrow-back-content {
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
}

.arrow-back {
  left: 0;
  height: 34px;
  width: 44px;
}

.arrow-back-content {
  top: 0px;
  left: 37px;
  bottom: 0;
  width: 30px;
}

.toggle-wrapper {
  /* transition: all cubic-bezier(0.075, 0.82, 0.165, 1); */
  z-index: 100001;
  position: absolute;
  left: 0px;
  top: 0;
  bottom: 0;
  width: 35px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.content-left-container {
  display: flex;
  flex-grow: 1;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  margin-right: 4px;
  justify-content: flex-start;
}

.content-left-container * {
  width: inherit;
}

.content-right-container {
  display: flex;
  flex-grow: 2;
  align-items: center;
  justify-content: flex-end;
}

.content-right-container * {
  width: inherit;
}

.border-right {
  display: flex;
  position: relative;
  overflow: hidden;
  width: 1px;
  height: calc(100% - 12px);
  align-self: center;
}

.two-rows {
  width: calc(100% - 0px);
  /* padding-right: 10px; */
  display: flex;
  flex-direction: column;
}

.two-rows * {
  text-overflow: ellipsis;
  overflow-x: hidden;
}
</style>
